import React from 'react';
import { useTranslation } from 'react-i18next';
import { translateBlockValue } from 'i18n';
import Typography from '@mui/material/Typography';
import AgGridReact from 'ui/AgGridReact/AgGridReact';
import { BudgetBys, DEFAULT_BUDGET_BY } from 'business/constants';
import TextFloatingFilter from 'ui/AgGridReact/TextFloatingFilter';
import { ResourceBudgetProposalAssociationT } from 'routes/BudgetModule/config/types';
import {
    getResourceNameField,
    isActiveBudgetProposal,
} from 'routes/BudgetModule/helpers/budgetProposalHelpers';
import { styled } from '@mui/material/styles';
import { Box } from '@mui/material';
import { ColDef, GridReadyEvent } from 'ag-grid-community';
import { PADDING_TITLE } from './helpers/constants';
import { useSelectSecondaryBudgetAxisSourceTable } from 'routes/BudgetModule/components/BudgetWithProvider/SecondaryBudgetAxisSourceTableContext';

type Props = {
    budgetProposalResources: ResourceBudgetProposalAssociationT[];
    budgetBy: BudgetBys;
};

const StyGrid = styled(Box, {
    name: 'StyGrid',
})({
    width: '70%',
    marginBottom: 0,
    '&&& .ag-cell': {
        fontSize: '0.85em',
    },
});

export default function OperationBudgetProposalByResource({
    budgetProposalResources,
    budgetBy = DEFAULT_BUDGET_BY,
}: Props): React.ReactElement {
    const { t } = useTranslation();

    const resourceNameField = getResourceNameField(budgetBy);
    const sourceTable = useSelectSecondaryBudgetAxisSourceTable();

    // From the queried gestionnaires, we find which budget proposal each belongs to
    const dataToDisplay = budgetProposalResources.filter(
        (el) => el.budgetProposal && isActiveBudgetProposal(el.budgetProposal),
    );

    const onGridReady = (params: GridReadyEvent) => {
        params.api.sizeColumnsToFit();
        params.api.resetRowHeights();
        params.api.setGridOption('domLayout', 'autoHeight');
    };

    const resourceName = translateBlockValue(resourceNameField, true);
    const columns: ColDef[] = [
        {
            field: 'resource',
            headerName: resourceName,
            sort: 'desc',
            valueGetter: ({ data }) => data.resource?.resourceName,
        },
        {
            field: 'budgetProposal',
            headerName: t('secondaryBudgetAxisElement360.budgetProposal'),
            valueGetter: ({ data }) => data.budgetProposal?.name,
        },
    ];

    const colDefs: ColDef[] = columns.map((column) => {
        const cols = {
            resizable: true,
            ...column,
            filter: 'agTextColumnFilter',
            sortable: true,
            floatingFilter: true,
            floatingFilterComponent: 'textFloatingFilter',
            suppressFloatingFilterButton: true,
            menuTabs: [
                'filterMenuTab' as const,
                'generalMenuTab' as const,
                'columnsMenuTab' as const,
            ],
            cellStyle: {
                display: 'flex',
                alignItems: 'center',
            },
        };
        return { key: column.field, ...cols };
    });

    return (
        <StyGrid className="ag-theme-material">
            <Typography sx={{ ...PADDING_TITLE }} variant="h5">
                {t('secondaryBudgetAxisElement360.budgetProposalByResource', {
                    dimension: `${resourceName}s`,
                    context: sourceTable,
                })}
            </Typography>
            <AgGridReact
                headerHeight={40}
                rowHeight={36}
                rowData={dataToDisplay}
                columnDefs={colDefs}
                components={{
                    textFloatingFilter: TextFloatingFilter,
                }}
                onGridReady={onGridReady}
            />
        </StyGrid>
    );
}
